import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { DepositSettingsForm } from "@/modules/settings/components/DepositSettingsForm";
import { Card } from "../primitives";

type DepositCardProps = {
  storeSlug: string;
  depositSettings: { type: "percent" | "flat"; amount: number } | null;
  includeShippingInOrderDeposit: boolean;
  unlocked: boolean;
  onRequestEnable: () => void;
};

// The deposit card. When no online method is configured it renders a
// locked-with-a-reason state that *teaches* the dependency (and offers a
// one-tap path to satisfy it) instead of hiding the feature.
export function DepositCard({
  storeSlug,
  depositSettings,
  includeShippingInOrderDeposit,
  unlocked,
  onRequestEnable,
}: DepositCardProps) {
  const t = useTranslations();
  const ss = keys.seller.storeSettings;

  if (!unlocked) {
    return (
      <div className="overflow-hidden rounded-xl border border-dashed border-[color:var(--border-strong)] bg-[color:var(--surface-alt)] px-4 py-4">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-muted)]"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="4" y="11" width="16" height="9" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-[color:var(--text-primary)]">
              {t(keys.seller.pmDepositLockedTitle)}
            </div>
            <p className="mt-0.5 text-xs leading-relaxed text-[color:var(--text-muted)]">
              {t(keys.seller.pmDepositLockedHelper)}
            </p>
            <button
              type="button"
              onClick={onRequestEnable}
              className="mt-2 text-xs font-semibold text-[color:var(--accent-600)] underline-offset-2 hover:underline"
            >
              {t(keys.seller.pmDepositEnableCta)} →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card title={t(ss.depositTitle)} desc={t(ss.depositDesc)}>
      <DepositSettingsForm
        storeSlug={storeSlug}
        depositSettings={depositSettings}
        includeShippingInOrderDeposit={includeShippingInOrderDeposit}
      />
    </Card>
  );
}
